/**
 * Starred model ids — localStorage with configurable key per host app.
 */

export function createFavoritesStore(storageKey = "forgesuite.modelFavorites.v1") {
  function load(): string[] {
    if (typeof localStorage === "undefined") return [];
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return [];
      const parsed = JSON.parse(raw) as unknown;
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((x): x is string => typeof x === "string" && x.length > 0);
    } catch {
      return [];
    }
  }

  function persist(ids: string[]) {
    try {
      localStorage.setItem(storageKey, JSON.stringify(ids));
    } catch {
      /* quota */
    }
  }

  let ids = load();
  const listeners = new Set<(ids: string[]) => void>();

  function emit() {
    for (const l of listeners) l(ids);
  }

  return {
    get(): string[] {
      return ids;
    },
    has(modelId: string): boolean {
      return ids.includes(modelId);
    },
    subscribe(fn: (ids: string[]) => void): () => void {
      listeners.add(fn);
      fn(ids);
      return () => listeners.delete(fn);
    },
    toggle(modelId: string) {
      ids = ids.includes(modelId)
        ? ids.filter((id) => id !== modelId)
        : [modelId, ...ids];
      persist(ids);
      emit();
    },
    add(modelId: string) {
      if (ids.includes(modelId)) return;
      ids = [modelId, ...ids];
      persist(ids);
      emit();
    },
    remove(modelId: string) {
      ids = ids.filter((id) => id !== modelId);
      persist(ids);
      emit();
    },
    clear() {
      ids = [];
      persist(ids);
      emit();
    },
  };
}

export type FavoritesStore = ReturnType<typeof createFavoritesStore>;
